<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="schedule-screener-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="schedule-screener-form" @submit.prevent="onSubmit">
        <div class="schedule-screener-form-body">
          <v-toolbar
            flat
            height="76"
            class="schedule-screener__toolbar"
          >
            <v-toolbar-title class="schedule-screener-toolbar-title">Schedule screener</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              color="accent"
              icon
              tile
              @click="closeDialog"
            >
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-tabs-items v-model="tab" class="schedule-screener__tabs-items">
            <v-tab-item class="schedule-screener__tab">
              <v-container class="schedule-screener-card-container">
                <v-row>
                  <v-col sm="12" class="pb-0">
                    <v-text-field
                      :value="defaultQueryParams.query"
                      class="bh-input-pill rounded-pill"
                      color="accent"
                      height="48px"
                      placeholder="Search for participants"
                      filled
                      clearable
                      hide-details
                      rounded
                      @input="onSearch('query', $event)"
                      @click:clear="clearSearch"
                      @click:append="runSearch"
                      @keydown.enter="runSearch"
                    >
                      <template v-slot:append>
                          <v-progress-circular
                            v-if="loading"
                            size="16"
                            width="2"
                            color="info"
                            indeterminate
                          ></v-progress-circular>
                          <v-icon v-else>bhi-loupe</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="selectedParticipantsData.length>0">
                  <v-col>
                    <div class="schedule-screener__avatars">
                      <deletable-avatar v-for="participant in selectedParticipantsData" :key="participant.id" :user="participant" @remove="chooseParticipant(participant)" />
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col sm="12" class="">
                    <main-user-table
                      :headers="headers"
                      :items="computedParticipants"
                      :items-per-page="queryParams.pageSize"
                      :server-items-length="dialogPagination.totalCount || -1"
                      :page="queryParams.page"
                      :row-component="'participant-selectable-table-row'"
                      @to-user="chooseParticipant"
                      @action:get-data="getParticipants"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item class="schedule-screener__tab">
              <div class="schedule-screener__selected">
                <v-container v-if="selectedParticipantsData.length > 0" class="schedule-screener-card-container">
                  <h5>Selected participants</h5>
                    <div class="schedule-screener__avatars">
                      <deletable-avatar v-for="participant in selectedParticipantsData" :key="participant.id" :user="participant" @remove="chooseParticipant(participant)" />
                    </div>
                </v-container>
                <v-container class="schedule-screener-card-container">
                  <v-row>
                    <v-col>
                      <dialog-select label="Select screener" name="surveyDefinitionId" :items="screenerTemplates" :value="screenerTemplates && screenerTemplates.length>0 ? screenerTemplates[0] : null" :itemText="'title'" @input="onInput"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      <dialog-date-picker label="Date" name="date" :value="scheduleData.date|moment('MM/DD/YYYY')" @input="onInput" />
                    </v-col>
                    <v-col cols="6">
                      <dialog-time-picker label="Time" name="time" :initialTime="scheduleData.time|moment('hh:mm A')" @input="onInput" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <dialog-input useNumbers label="Expires in (days)" :value="scheduleData.expiration" name="expiration" @input="onInput"/>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-tab-item>
          </v-tabs-items>
          <v-container class="d-flex justify-space-between align-center schedule-screener__tab--footer">
            <v-btn
              rounded
              color="primary"
              elevation="0"
              :disabled="tab == 0" 
              @click="prevStep">
              Prev
            </v-btn>
            <div class="schedule-screener__dots">
              <span :class="{'active': tab == 0}"></span>
              <span :class="{'active': tab == 1}"></span>
            </div>
            <v-btn
              rounded
              color="primary"
              elevation="0"
              v-if="tab != 1"
              @click="tab++">
              Next
            </v-btn>
            <v-btn
              rounded
              color="primary"
              elevation="0"
              @click="onSubmit(false)"
              :disabled="!formIsValidCheck"
              v-if="tab == 1">
              Schedule
            </v-btn>
          </v-container>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable'
import DeletableAvatar from '@/components/avatars/DeletableAvatar'
import DialogInput from '@/components/forms/DialogInput'
import DialogSelect from '@/components/forms/DialogSelect'
import DialogDatePicker from '@/components/forms/DialogDatePicker'
import DialogTimePicker from '@/components/forms/DialogTimePicker'

const emptyForm = {
}
const today = new Date()

export default {
  name: 'ScheduleScreenerDialog',
  components: {
    MainUserTable,
    DeletableAvatar,
    DialogInput,
    DialogSelect,
    DialogDatePicker,
    DialogTimePicker,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    searchedParticipants: {
      type: Array,
      default: ()=>[],
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  mixins: [RouterMixin],
  data () {
    return {
      dialog: false,
      tab: 0,
      headers: [],
      selectedParticipantsData: [],
      defaultQueryParams: {
        pageSize: 50,
        page: 0,
        sortBy: 'creationDate',
        direction: 'desc',
        isActive: true
      },
      scheduleData: {
        date: this.$moment(today).format('MM/DD/YYYY'),
        time: '12',
        expiration: 5
      },
    }
  },
  watch: {
    showDialog: function (val) {
      if (val) {
        this.dialog = true
        this.getParticipants({})
        this.getScreenerTemplates({}).then(()=>{
          if (this.screenerTemplates?.length>0) this.scheduleData.surveyDefinitionId = this.screenerTemplates[0].id
        })
      } else {
        this.dialog = false
      }
    },
  },
  mounted() {
  },
  computed: {
    ...mapState('patientsStates', [
      'dialogPatients',
      'dialogPagination'
    ]),
    ...mapState('screenersStates', [
      'screenerTemplates'
    ]),
    formIsValidCheck () {
      if (this.selectedParticipantsData.length == 0) {
        return false
      }

      return true
    },
    computedParticipants () {
      let participants = []
      this.dialogPatients.forEach(item=>{
        let selected = false
        if (this.selectedParticipantsData.findIndex((participant)=>participant.id == item.id)>-1) { selected = true
        }
        participants.push({selected: selected, ...item})
      })
      return participants
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getDialogPatients',
    ]),
    ...mapActions('screenersStates', [
      'getScreenerTemplates',
    ]),
    getParticipants(data) {
      debounce(()=>{
        this.getDataAction({...this.queryParams, ...data}, false)
      }, 500, 'getParticipants')
    },
    getData(data = {}) {
      if (this.dialog) this.getDialogPatients(data)
    },
    closeDialog () {
      this.scheduleData = {}
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === "Escape" && key.isTrusted) this.closeDialog()
    },
    onSearch (key, value) {
      this.defaultQueryParams.fullName = value
      debounce(() => {
        this.getDataAction({ ...this.queryParams, fullName: value }, false)
      }, 1000, 'participantSearch')
    },
    clearSearch() {
      this.defaultQueryParams.fullName = ''
      this.getDataAction({ ...this.queryParams }, false)
    },
    runSearch() {
      if (this.defaultQueryParams.fullName)
        this.onSearch('fullName', this.defaultQueryParams.fullName)
    },
    chooseParticipant(participant) {
      debounce(() => {
        let i = this.selectedParticipantsData.findIndex((selected)=>selected.id == participant.id)
        if (i !== -1) {
          this.selectedParticipantsData.splice(i, 1)
        }
        else {
          this.selectedParticipantsData.push(participant)
        }
      }, 100, 'chooseParticipant')
    },
    prevStep() {
      this.tab > 0 ? this.tab-- : this.tab = 0
    },
    onSubmit() {
      this.scheduleData.participants = this.selectedParticipantsData.map((participant)=>{
        return participant.id
      })
      const tmpDateTime = this.$moment(this.scheduleData.date+' '+this.scheduleData.time).format("YYYY-MM-DDTHH:mm");
      this.scheduleData.date = tmpDateTime
      this.$emit('on-submit', this.scheduleData)
    },
    onInput({ name, value }) {
      this.scheduleData[name] = value
    }

  }
}
</script>

<style lang="scss">
@import "@/styles/variables.scss";

.schedule-screener {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2*$gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-partial {
    flex-grow: 1;
  }
  &__toolbar {
    flex-grow: 0;
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &__tabs {
    flex-grow: 0;
  }
  &__tabs-items {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    background:$bh-blue--4 !important;
    .v-window__container {
      min-height: 100%;
      overflow-y: auto;
    }
    .v-window-item {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
    }
  }
  &__avatars {
    display: flex;
    flex-wrap: wrap;
  }
  &--list {
    background-color: $bh-blue--4 !important;
    &-item {
      flex-direction: column;
      background-color:$bh-white;
      border-radius: 8px;
      align-items: stretch;
      margin-bottom: 10px;
      padding: 8px 16px;
      p {
        margin-bottom: 0;
      }
      &::after {
        display: none;
      }
    }
  }
  &--desc {
    display: flex;
    align-items: center;
    h5 {
      margin-bottom: 0;
    }
  }
  &__tab {
    height: 100%;
    background-color: $bh-blue--4;
    display: flex;
    flex-direction: column;
    & > .v-tabs-items {
      overflow: auto;
      flex-grow: 1;
      background-color: $bh-blue--4;
    }
    .v-input--switch {
      margin: 0;
    }
    .v-label {
      font-size: 12px;
      color: $bh-gray--2;
    }
  }
  &__dots {
    span {
      width: 16px;
      height: 16px;
      display: inline-block;
      border-radius: 50%;
      background-color:$bh-white;
      margin:0 3px;
      transition-duration: 300ms;
      &.active {
        background-color:$bh-blue;
      }
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5*$gutter;
      padding-right: 1.5*$gutter;
    }
    &-container {
      padding-left: 1.5*$gutter;
      padding-right: 1.5*$gutter;
      h5 {
        margin-top: $gutter/2;
        margin-bottom: $gutter/4;
      }
    }
    &-footer {
      padding: 1.5*$gutter;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5*$gutter;
        padding-right: 1.5*$gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25*$gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25*$gutter
    }
  }
}
</style>
